import React, { useState } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import {
  Card,
  Grid,
  Box,
  Typography,
  Snackbar,
  styled,
  Button,
  CircularProgress,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { MatxLayoutSettings } from "app/components/MatxLayout/settings";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import {
  changePhoneNumberRequestApproval,
  requestedUserDetails,
} from "services/api/members";
import { useEffect } from "react";
import { FormatDate } from "../commons/commons";
import NotFound from "../sessions/NotFound";

// STYLED COMPONENTS
const FlexBox = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
}));

const JustifyBox = styled(FlexBox)(() => ({
  justifyContent: "center",
}));

const RequestModal = styled(JustifyBox)(() => ({
  
  backgroundSize: "cover",
  backgroundPosition: "center",
  minHeight: "100vh !important",
  "& .card": {
    maxWidth: 500,
    minHeight: 300,
    margin: "1rem",
    display: "flex",
    borderRadius: 12,
    alignItems: "center",
  },
}));

export default function ViewRequest() {
  const navigate = useNavigate();
  const { id, requestId, action } = useParams();
  const login = localStorage.getItem("logIn");
  const userRole = localStorage.getItem("userRole");
  const [userData, setUserData] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarColor, setSnackbarColor] = useState();
  const [loading, setLoading] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  useEffect(() => {
    if (id && login === "true" && userRole) {
      getUserDetails();
    } else {
      navigate("/", {
        state: {
          openLoginPopup: true,
          loginType: "admin",
          redirectAfterLogin: {
            id,
            requestId,
            action,
          },
        },
      });
    }
  }, [userRole]);

  const getUserDetails = async () => {
    setLoading(true);
    try {
      const payload = { userId: id, requestId: requestId };
      const response = await requestedUserDetails(payload);

      if (response?.status === 200 || response?.status === 201) {
        setUserData(response?.data?.data?.records[0]);
      } else {
        setSnackbarMessage("Failed to fetch requested details!");
        setSnackbarColor(MatxLayoutSettings.palette.danger);
        setSnackbarOpen(true);
      }
    } catch (err) {
      setSnackbarMessage("Error fetching requested details");
      setSnackbarColor(MatxLayoutSettings.palette.danger);
      setSnackbarOpen(true);
      console.error("Error fetching requested details", err);
    } finally {
      setLoading(false);
      setInitialLoadComplete(true);
    }
  };

  const handleConfirmation = async () => {
    setLoading(true);

    const payload = {
      userId: id,
      requestId: requestId,
      action: action,
    };

    changePhoneNumberRequestApproval(payload).then((res) => {
      if (res?.status === 200 || res?.status === 201) {
        setLoading(false);
        setSnackbarMessage(
          action === "approve"
            ? "Request successfully approved!"
            : "Request successfully rejected!"
        );
        setSnackbarColor(
          action === "approve"
            ? MatxLayoutSettings.palette.green
            : MatxLayoutSettings.palette.danger
        );
        setSnackbarOpen(true);
        setTimeout(() => {
          navigate("/events");
        }, 2000);
      } else {
        setLoading(false);
        setSnackbarMessage(() => {
          const actionError =
            res?.data?.error?.validations?.action?.[0]?.message;

          return (
            (actionError ? `${actionError}!` : null) ??
            "Failed to perform the action requested! Try again!"
          );
        });
        setSnackbarColor(MatxLayoutSettings.palette.danger);
        setSnackbarOpen(true);
        setTimeout(() => {
          navigate("/events");
        }, 2000);
      }
    });
  };

  const handleCancel = () => {
    navigate("/events");
  };

  const handleBackNavigation = () => {
    navigate(-1);
  };

  if (!initialLoadComplete) {
    return (
      <RequestModal>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="50vh"
        >
          <CircularProgress
            size={60}
            thickness={4}
            sx={{ color: MatxLayoutSettings.palette.active }}
          />
        </Box>
      </RequestModal>
    );
  }

  return (
    <RequestModal>
      {userRole === "ADMIN" && userData ? (
        <Card className="card">
          <Grid container>
            <Grid item sm={12} xs={12}>
              <Box p={4} height="100%">
                <FlexBox sx={{ mb: 2, alignItems: "center" }}>
                  <ArrowBackIosIcon
                    sx={{
                      cursor: "pointer",
                      margin: 0,
                      padding: 0,
                      fontSize: "20px",
                    }}
                    onClick={handleBackNavigation}
                  />
                  <Typography
                    variant="h6"
                    sx={{
                      textAlign: "center",
                      color: MatxLayoutSettings?.palette?.black,
                      flex: 1,
                    }}
                  >
                    <b>Request Confirmation</b>
                  </Typography>
                </FlexBox>

                <Box
                  sx={{
                    border: `1px solid ${MatxLayoutSettings.palette.greySecondary}`,
                    borderRadius: 2,
                    p: 2,
                  }}
                >
                  <Typography
                    variant="body1"
                    fontWeight={500}
                    fontSize="16px"
                    sx={{
                      color: MatxLayoutSettings?.palette?.active,
                      flex: 1,
                      mb: 2,
                    }}
                  >
                    <b>User Details</b>
                  </Typography>

                  <FlexBox justifyContent="flex-start" gap={1}>
                    <Typography
                      variant="body1"
                      fontWeight={500}
                      fontSize="14px"
                      sx={{
                        color: MatxLayoutSettings?.palette?.black,
                        mb: 2,
                      }}
                    >
                      <b>User Name:</b>
                    </Typography>

                    <Typography
                      variant="body1"
                      sx={{
                        color: MatxLayoutSettings?.palette?.black,
                        mb: 2,
                        fontSize: "14px",
                      }}
                    >
                      {userData?.username}
                    </Typography>
                  </FlexBox>

                  <FlexBox justifyContent="flex-start" gap={1}>
                    <Typography
                      variant="body1"
                      fontWeight={500}
                      fontSize="14px"
                      sx={{
                        color: MatxLayoutSettings?.palette?.black,
                        mb: 2,
                      }}
                    >
                      <b>Previous Phone Number:</b>
                    </Typography>

                    <Typography
                      variant="body1"
                      sx={{
                        color: MatxLayoutSettings?.palette?.black,
                        mb: 2,
                        fontSize: "14px",
                      }}
                    >
                      {userData?.previous_phone_number}
                    </Typography>
                  </FlexBox>

                  <FlexBox justifyContent="flex-start" gap={1}>
                    <Typography
                      variant="body1"
                      fontWeight={500}
                      fontSize="14px"
                      sx={{
                        color: MatxLayoutSettings?.palette?.black,
                        mb: 2,
                      }}
                    >
                      <b>New Phone Number:</b>
                    </Typography>

                    <Typography
                      variant="body1"
                      sx={{
                        color: MatxLayoutSettings?.palette?.black,
                        mb: 2,
                        fontSize: "14px",
                      }}
                    >
                      {userData?.request_phone_number}
                    </Typography>
                  </FlexBox>

                  <FlexBox justifyContent="flex-start" gap={1}>
                    <Typography
                      variant="body1"
                      fontWeight={500}
                      fontSize="14px"
                      sx={{
                        color: MatxLayoutSettings?.palette?.black,
                        mb: 2,
                      }}
                    >
                      <b>Requested Date:</b>
                    </Typography>

                    <Typography
                      variant="body1"
                      sx={{
                        color: MatxLayoutSettings?.palette?.black,
                        mb: 2,
                        fontSize: "14px",
                      }}
                    >
                      {FormatDate(userData?.created_at)}
                    </Typography>
                  </FlexBox>
                </Box>

                <Typography
                  variant="body1"
                  sx={{
                    textAlign: "center",
                    color: MatxLayoutSettings?.palette?.black,
                    mt: 2,
                    mb: 2,
                    fontSize: "16px",
                    fontWeight: "100",
                  }}
                >
                  Are you sure, You want to confirm{" "}
                  {action === "approve" ? "Approval" : "Rejection"} ?
                </Typography>

                <Box display="flex" gap={2}>
                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    sx={{
                      mb: 2,
                      mt: 2,
                      backgroundColor: MatxLayoutSettings?.palette?.primary,
                      border: "1px solid",
                      borderColor: MatxLayoutSettings?.palette?.active,
                      color: MatxLayoutSettings?.palette?.active,
                      "&:hover": {
                        backgroundColor: MatxLayoutSettings?.palette?.active,
                        color: MatxLayoutSettings?.palette?.primary,
                      },
                    }}
                    onClick={handleCancel}
                  >
                    Cancel
                  </Button>

                  <LoadingButton
                    type="submit"
                    variant="contained"
                    fullWidth
                    loading={loading}
                    sx={{
                      mb: 2,
                      mt: 2,
                      backgroundColor: MatxLayoutSettings?.palette?.active,
                      border: "1px solid",
                      borderColor: MatxLayoutSettings?.palette?.active,
                      "&:hover": {
                        backgroundColor: MatxLayoutSettings?.palette?.primary,
                        color: MatxLayoutSettings?.palette?.active,
                      },
                    }}
                    onClick={handleConfirmation}
                  >
                    {action === "approve" ? "Approve" : "Reject"}
                  </LoadingButton>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Card>
      ) : (
        <NotFound />
      )}

      <Snackbar
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        message={snackbarMessage}
        autoHideDuration={2000}
        ContentProps={{
          sx: {
            backgroundColor: snackbarColor,
            color: MatxLayoutSettings.palette.white,
          },
        }}
      />
    </RequestModal>
  );
}
